import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import AbstractIframeCheckout from '@bambora/checkout-sdk-web/dist/types/abstractIframeCheckout';

import { IPaymentIntent, ngHelper } from '@cakeiteasy/web-utils';
import { SentryLoggerService } from '@cakeiteasy/web-utils';

import {IWebhookUrls} from '../../../payment/models/i-payment-models';
import {PAYMENT_PLATFORM_TYPES} from '../../../payment/payment.constants';
import {PaymentApiService} from '../../../api-services/services/payment-api.service';
import {KlarnaCheckoutService} from '../../../payment/services/klarna-checkout.service';
import {BamboraCheckoutService} from '../../../payment/services/bambora-checkout.service';

@Component({
  standalone: false,
  selector: 'payment-request-details',
  templateUrl: './payment-request-details.component.html',
  styleUrls: ['./payment-request-details.component.scss']
})
export class PaymentRequestDetailsComponent implements OnChanges, OnInit, OnDestroy {

  @Input() paymentIntent: IPaymentIntent;
  @Input() cancelPaymentOnClose: boolean = true;
  @Input() embedBambora: boolean = false;
  @Input() webhookUrlPrefix: string;

  @Output() onPaymentError = new EventEmitter<any>();

  public paymentMethods = [];

  public bamboraCheckout: AbstractIframeCheckout<any>;
  public klarnaSnippet: any;

  public currentPaymentType: string;
  public requestFrom: string;
  public PAYMENT_PLATFORM_TYPES = PAYMENT_PLATFORM_TYPES;
  public pPayment: Promise<any>;
  public snippetLoading: Promise<any>;
  public error: string;

  constructor(private _bambora: BamboraCheckoutService,
              private _klarna: KlarnaCheckoutService,
              private _translateService: TranslateService,
              private _paymentApi: PaymentApiService,
              private _sentryLogger: SentryLoggerService,
              private _route: ActivatedRoute) {
    const bambora = {
      icon: 'icon-credit-card',
      title: this._translateService.instant('orderProcess.payment.creditCardPayment.title'),
      info: this._translateService.instant('orderProcess.payment.creditCardPayment.info'),
      type: this.PAYMENT_PLATFORM_TYPES.bambora
    };

    const klarna = {
      icon: 'icon-doc-list',
      title: this._translateService.instant('orderProcess.payment.invoiceKlarna.title'),
      info: this._translateService.instant('orderProcess.payment.invoiceKlarna.info'),
      type: this.PAYMENT_PLATFORM_TYPES.klarna
    };

    this.paymentMethods = [bambora, klarna];
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'paymentIntent', (newIntent: IPaymentIntent, oldIntent) => {
      if (!newIntent) {
        return;
      }

      this.requestFrom = (newIntent.order.multiOrder as any).source === 'WEB_SHOP'
        ? newIntent.order.bakery.name
        : 'Cake it easy';
    });
  }

  ngOnInit(): void {
    this.error = this._route.snapshot.queryParams.error;
  }

  public ngOnDestroy(): void {
    if (this.bamboraCheckout) {
      this.bamboraCheckout.destroy();
    }
  }

  public pay(method: string, $event: MouseEvent) {
    this.error = '';

    switch (method) {
      case PAYMENT_PLATFORM_TYPES.bambora: {
        return this._createExtraPayment(method)
          .then((data) => this._processBamboraSnippet(data, $event))
          .catch((error) => this.onPaymentError.emit(error));
      }
      case PAYMENT_PLATFORM_TYPES.klarna: {
        let snippetResolver = Promise.resolve(this.klarnaSnippet);

        if (!this.klarnaSnippet) {
          snippetResolver = this._createExtraPayment(method)
            .catch((error) => this.onPaymentError.emit(error));
        }

        return this.pPayment = snippetResolver
          .then((data) => {
            if (data && data.snippet) {
              return this._putSnippet(data.snippet);
            }
          });
      }
    }
  }

  private _createExtraPayment(method: string) {
    const { token } = this.paymentIntent;

    const url = `${location.protocol}//${location.host}${this.webhookUrlPrefix}order-extra-payment?token=${token}`;

    const webhooks: IWebhookUrls = {
      rejectUrl: url + '&error=true',
      successUrl: url + '&success=true'
    };

    return this.pPayment = firstValueFrom(this._paymentApi
      .createExtraPayment(this.paymentIntent.order.multiOrder.id,
        this.paymentIntent.id,
        method.toLowerCase(),
        webhooks));
  }

  private _processBamboraSnippet(data: any, $event: MouseEvent) {
    const onError = (error: any) => {
      this._sentryLogger.addBreadcrumbs({
        category: 'Bambora payment',
        level: 'error',
        data: data
      });
      if (error instanceof HttpErrorResponse) {
        this._sentryLogger.httpError(error);
      } else {
        this._sentryLogger.captureException(error);
      }
    };

    this.bamboraCheckout = this._bambora.createCheckout(data, onError.bind(this), $event);

    const onSuccess = (payload) => {
      if (payload && payload.acceptUrl) {
        location.href = payload.acceptUrl;
      }
    };

    const onCancel = (error) => {
      this.currentPaymentType = '';
      console.log(error, 'cancelled');
    };

    this._bambora.subscribeOnEvents(this.bamboraCheckout, onSuccess.bind(this), onCancel.bind(this));
  }

  private _putSnippet(snippet) {
    // This is necessary otherwise the scripts tags are not going to be evaluated
    const container = document.querySelector('[data-snippet]');

    this._klarna.insertSnippet(snippet, container);
  }
}
